// the terms of service page - like the privacy page, it's just static legal text
import React from 'react';
// Link navigates between our pages without reloading the whole browser
import { Link } from 'react-router-dom';
// same shared stylesheet the privacy page uses
import './LegalPage.css';
import { usePageMeta } from '../hooks/usePageMeta';

// change this whenever the terms are updated; it's displayed under the title
const LAST_UPDATED = 'August 4, 2026';

export default function TermsPage() {
  usePageMeta({
    title: 'Terms of Service - GPTDisguise',
    description: 'The terms that apply when you use the GPTDisguise browser extension and website.',
    path: '/terms'
  });

  // no state, no logic - just render the fixed content below
  return (
    <div className="legal-page">
      {/* NAV - logo + a back-to-home link, same as the privacy page */}
      <nav className="legal-nav">
        <a href="/" className="legal-nav-logo">
          <img src="/icon.png" alt="GPTDisguise icon" className="legal-nav-icon" />
          GPT<span>Disguise</span>
        </a>
        <Link to="/" className="legal-nav-back">← Back to home</Link>
      </nav>

      {/* title, the last-updated date, then each numbered section of the terms */}
      <main className="legal-content">
        <p className="legal-eyebrow">Legal</p>
        <h1>Terms of Service</h1>
        {/* uses the constant above so the date only lives in one spot */}
        <p className="legal-updated">Last updated: {LAST_UPDATED}</p>

        <p className="legal-lead">
          These Terms of Service (“Terms”) govern your use of the GPTDisguise
          browser extension, website, and related services (together, the
          “Service”). By creating an account or using the Service, you agree to
          these Terms. If you do not agree, please do not use the Service.
        </p>

        <section>
          <h2>1. The Service</h2>
          <p>
            GPTDisguise is a browser extension that visually re-skins AI chat
            interfaces (such as ChatGPT, Claude, and Gemini) to resemble other
            applications like Google Docs. The disguise is cosmetic and runs locally
            in your browser. We provide the Service “as is” and may add, change, or
            remove features over time.
          </p>
        </section>

        <section>
          <h2>2. Accounts</h2>
          <p>
            Some features require an account. You are responsible for the
            information you provide, for keeping your login credentials secure, and
            for all activity under your account. You must be at least 13 years old
            (or the age of digital consent in your country) to use the Service.
          </p>
        </section>

        <section>
          <h2>3. Premium subscriptions and billing</h2>
          <ul>
            <li>
              <strong>Plans.</strong> The Service offers a Free plan and a paid
              Premium plan. Premium unlocks features such as additional disguise
              themes, keyboard shortcuts, and early-access integrations.
            </li>
            <li>
              <strong>Payment.</strong> Premium is billed through Stripe on a
              recurring basis (currently monthly) until cancelled. By subscribing,
              you authorize us and Stripe to charge your payment method for each
              billing cycle.
            </li>
            <li>
              <strong>Trials.</strong> If a free trial is offered, you will be
              charged automatically when the trial ends unless you cancel before
              then.
            </li>
            <li>
              <strong>Cancellation.</strong> You may cancel at any time. Cancellation
              stops future charges; access to Premium continues until the end of the
              current billing period.
            </li>
            <li>
              <strong>Refunds.</strong> Except where required by law, payments are
              non-refundable. If you believe you were charged in error, contact us
              and we will review in good faith.
            </li>
            <li>
              <strong>Price changes.</strong> We may change subscription pricing.
              Changes apply to future billing periods and we will give reasonable
              notice.
            </li>
          </ul>
        </section>

        <section>
          <h2>4. Acceptable use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Service for any unlawful purpose or to deceive in a way that causes harm or violates the rights of others.</li>
            <li>Reverse engineer, resell, or redistribute the Service except as permitted by law.</li>
            <li>Interfere with, disrupt, or attempt to gain unauthorized access to the Service or its underlying systems.</li>
            <li>Circumvent the Premium paywall or share access in violation of these Terms.</li>
          </ul>
        </section>

        <section>
          <h2>5. Third-party services</h2>
          <p>
            The Service interacts with third-party platforms (including ChatGPT,
            Claude, Gemini, and others) and relies on providers such as Supabase,
            Stripe, and Vercel. Your use of those platforms is subject to their own
            terms. GPTDisguise is an independent product and is not affiliated with,
            endorsed by, or sponsored by OpenAI, Anthropic, Google, or Microsoft.
            You are responsible for complying with the terms of any platform you use
            the disguise on.
          </p>
        </section>

        <section>
          <h2>6. Intellectual property</h2>
          <p>
            The Service, including its software, branding, and content, is owned by
            GPTDisguise / Cloak Studio and protected by intellectual property laws.
            We grant you a limited, non-exclusive, non-transferable, revocable
            license to use the Service for personal, lawful purposes. All trademarks
            referenced belong to their respective owners.
          </p>
        </section>

        <section>
          <h2>7. Disclaimer of warranties</h2>
          <p>
            The Service is provided “as is” and “as available,” without warranties of
            any kind, whether express or implied, including fitness for a particular
            purpose and non-infringement. We do not warrant that the Service will be
            uninterrupted, error-free, or that the disguise will be undetectable in
            any given context.
          </p>
        </section>

        <section>
          <h2>8. Limitation of liability</h2>
          <p>
            To the maximum extent permitted by law, GPTDisguise and its operators
            will not be liable for any indirect, incidental, special, consequential,
            or punitive damages, or for any loss of data, profits, or goodwill
            arising from your use of the Service. Our total liability for any claim
            relating to the Service will not exceed the amount you paid us in the
            twelve months preceding the claim.
          </p>
        </section>

        <section>
          <h2>9. Termination</h2>
          <p>
            You may stop using the Service and delete your account at any time. We
            may suspend or terminate your access if you violate these Terms or if we
            discontinue the Service. Provisions that by their nature should survive
            termination (such as intellectual property, disclaimers, and limitation
            of liability) will survive.
          </p>
        </section>

        <section>
          <h2>10. Changes to these Terms</h2>
          <p>
            We may update these Terms from time to time. When we do, we will revise
            the “Last updated” date above. Continued use of the Service after changes
            take effect constitutes acceptance of the revised Terms.
          </p>
        </section>

        <section>
          <h2>11. Contact</h2>
          <p>
            Questions about these Terms? Email{' '}
            <a href="mailto:cloakstudios@outlook.com">cloakstudios@outlook.com</a>.
          </p>
        </section>

        <p className="legal-footnote">
          GPTDisguise · Cloak Studio · Not affiliated with OpenAI, Anthropic,
          Google, or Microsoft.
        </p>
      </main>
    </div>
  );
}
